import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  CloudCheckIcon,
  LoaderCircleIcon,
  RefreshCwIcon,
} from '@buffer-mono/popcorn'

import styles from './SyncTriggerIcon.module.css'

/**
 * Visual state of the sync control in the Community header.
 *
 * - `upToDate` is reserved for engagement that arrives on its own; it is the
 *   only green state, so green always means "nothing to do here".
 * - `synced` and `syncAvailable` both describe polled engagement, and differ
 *   only in whether a manual sync is currently worth offering.
 * - `checking` covers an in-flight backfill.
 */
export type SyncIconState = 'upToDate' | 'synced' | 'syncAvailable' | 'checking'

const ICONS: Record<SyncIconState, typeof CloudCheckIcon> = {
  upToDate: CloudCheckIcon,
  synced: CloudCheckIcon,
  syncAvailable: RefreshCwIcon,
  checking: LoaderCircleIcon,
}

export const SyncTriggerIcon = ({
  state,
  className,
}: {
  state: SyncIconState
  className?: string
}): JSX.Element => {
  const Icon = ICONS[state]

  return (
    <div
      data-testid="sync-status-icon"
      data-sync-state={state}
      className={clsx(
        styles.container,
        state === 'upToDate' ? styles.success : styles.neutral,
        className,
      )}
    >
      <Icon className={styles.icon} />
    </div>
  )
}
